import {
  ComposedSurveyPlugin,
  PluginHookName,
} from '@hyper-inferno/survey-elements-core';

export interface PluginEngineConfig {
  pluginDef: ComposedSurveyPlugin;
  sessionId: string;
  surveyId: string;
  initialRegistry: Record<string, any>;
  currentPageId?: number;
  /** Pass structural DOM container if running in a browser, otherwise defaults to null for Node.js */
  containerElement?: any;
  /** Unified event dispatch callback hook to update parent survey engine state changes */
  onStateChange?: (state: {
    usageId: string | number;
    data: any;
    isValid: boolean;
    error: string | null;
  }) => void;
  /** Hook called when the plugin successfully finishes its pre-submission lifecycle */
  onMarkCompleted?: (usageId: string | number) => void;
}

export class PluginRuntimeHost {
  private pluginDef: ComposedSurveyPlugin;
  private sessionId: string;
  private surveyId: string;
  private registry: Record<string, any>;
  private currentPageId?: number;
  private containerElement: any | null;

  // Handlers for updating external architecture orchestrators
  private onStateChange?: PluginEngineConfig['onStateChange'];
  private onMarkCompleted?: PluginEngineConfig['onMarkCompleted'];

  // Core internal plugin states mirroring your Angular reactive signals
  private pluginInternalState: Record<string, any> = {};
  private pluginValueData: any = undefined;
  private validationError: string | null = null;

  constructor(config: PluginEngineConfig) {
    this.pluginDef = config.pluginDef;
    this.sessionId = config.sessionId;
    this.surveyId = config.surveyId;
    this.registry = config.initialRegistry || {};
    this.currentPageId = config.currentPageId;
    this.containerElement = config.containerElement || null;
    this.onStateChange = config.onStateChange;
    this.onMarkCompleted = config.onMarkCompleted;

    // Hydrate the initial payload data (deep clone equivalent to JSON.parse/stringify)
    if (this.pluginDef.answerObject) {
      this.pluginValueData = structuredClone
        ? structuredClone(this.pluginDef.answerObject)
        : JSON.parse(JSON.stringify(this.pluginDef.answerObject));
    }
  }

  /**
   * Initializes the plugin state tracking. Equivalent to ngOnInit.
   */
  public async initialize(): Promise<void> {
    this.syncStateWithParent();

    // Trigger onPageInit cleanly inside an async-safe microtask queue wrapper
    await queueMicrotask(async () => {
      await this.triggerHook('onPageInit');
    });
  }

  /**
   * Handles dynamic registry data updates upstream. Equivalent to the onDataChange effect.
   */
  public async notifyDataChange(
    newRegistry: Record<string, any>,
  ): Promise<void> {
    this.registry = newRegistry;
    await this.triggerHook('onDataChange', this.registry);
  }

  /**
   * Handles the validation and submission flow. Equivalent to runSubmitHook.
   */
  public async executeBeforeSubmit(): Promise<boolean> {
    await this.triggerHook('onBeforeSubmit');
    return this.validationError === null;
  }

  /**
   * Cleans up local operational memory blocks. Equivalent to ngOnDestroy.
   */
  public async destroy(): Promise<void> {
    await this.triggerHook('onCleanup');
    this.pluginInternalState = {};
    this.pluginValueData = undefined;
    this.validationError = null;
  }

  // --- Getters to access standard payload outputs ---
  public getIsValid(): boolean {
    return this.validationError === null;
  }
  public getValidationError(): string | null {
    return this.validationError;
  }
  public getPluginData(): any {
    return this.pluginValueData;
  }

  /**
   * Isolated Script Runner and Execution Sandbox Trapper
   */
  public async triggerHook(
    hookName: PluginHookName,
    payload: any = null,
  ): Promise<void> {
    const script = this.pluginDef.hooks?.[hookName];
    if (!script) return;

    if (hookName === 'onBeforeSubmit') {
      this.validationError = null;
    }

    // Build the exact runtime sandbox execution context object context payload
    const executionContext = {
      config: this.pluginDef.instanceValues,
      registry: this.registry,
      container: this.containerElement, // Safely handles null on Node environments
      pageId: this.currentPageId,
      survey_context: {
        survey_id: this.surveyId,
        survey_session_id: this.sessionId,
        ...this.pluginDef.context,
      },
      state: {
        ...this.pluginInternalState,
      },

      setInvalid: (msg: string) => {
        this.validationError = msg;
        this.syncStateWithParent();
      },
      getAnswer: (id: string) => this.registry[id]?.data,
      patchData: (patch: Record<string, any>) => {
        this.pluginValueData = {
          ...this.pluginValueData,
          ...patch,
        };
        this.syncStateWithParent();
      },
      pluginData: () => this.pluginValueData,
    };

    try {
      // Isomorphic Safe Sandbox Layer using Proxy intercept wrappers
      const sandboxProxy = new Proxy(executionContext, {
        has() {
          return true;
        }, // Traps global lookups so expressions can't escape context scope
        get(target, prop) {
          if (prop === 'constructor') return undefined; // Protect against constructor prototypes escaping
          if (prop in target) {
            const val = target[prop as keyof typeof target];
            return typeof val === 'function' ? val.bind(target) : val;
          }
          return undefined;
        },
      });

      // Wrap standard runner dynamically with structural environment safety parameters (e.g., hidden globals)
      const executionWrapper = new Function(
        'context',
        'payload',
        `
        const window = undefined;
        const document = undefined;
        const process = undefined;
        const globalThis = undefined;
        
        with (context) {
          return (async () => {
            ${script}
          })();
        }
        `,
      );

      // Await the dynamic string execution payload synchronously or asynchronously
      await executionWrapper(sandboxProxy, payload);

      // Retain persistent memory data across sequential execution cycles
      this.pluginInternalState = executionContext.state;

      if (hookName === 'onBeforeSubmit') {
        this.syncStateWithParent();
        if (this.getIsValid() && this.onMarkCompleted) {
          this.onMarkCompleted(this.pluginDef.usageId);
        }
      }
    } catch (e) {
      console.error(
        `Plugin Execution Error Engine [${this.pluginDef.usageId}]:`,
        e,
      );
    }
  }

  /**
   * Syncs internal operational metadata metrics with external coordinator interfaces
   */
  private syncStateWithParent(): void {
    if (this.onStateChange) {
      this.onStateChange({
        usageId: this.pluginDef.usageId,
        data: this.pluginValueData,
        isValid: this.getIsValid(),
        error: this.validationError,
      });
    }
  }
}
